import {
  Divider,
  Flex,
  Form,
  PrimaryButton,
  Space,
  TertiaryButton,
  WithFormControlSelect,
} from '@procurify/ui'
import { FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'

import { CurrencySelect } from '@webapp/core/components/CurrencySelect'
import { type Currency } from '@webapp/core/entities/external/Currency'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

// Legacy PaymentTypes enum values (enum.definitions.js): 0 = Other, 1 = Check.
// ACH/EFT are excluded for credit-card-provider vendors (Angular vendor modal).
export interface PaymentTypeOption {
  value: number
  label: string
}

// View owns the form contract (Form Schema Ownership iron law). Select fields
// store full option objects.
export interface VendorPaymentMethodFormSchema {
  name: string
  type: Nullable<PaymentTypeOption>
  currency: Nullable<Currency>
}

export interface IVendorPaymentMethodFormProps {
  isSaving: boolean
  /** Card currency id — pre-selects the currency field (BHV-047). */
  defaultCurrencyId?: number
  onSubmit: (values: VendorPaymentMethodFormSchema) => void
  onCancel: () => void
}

// Form content ONLY — the "Add payment method to {provider}" ModalDialog
// wrapper is FS-owned (BHV-047).
export const VendorPaymentMethodForm = ({
  isSaving,
  defaultCurrencyId,
  onSubmit,
  onCancel,
}: IVendorPaymentMethodFormProps) => {
  const intl = useProcurifyIntl()

  const paymentTypeOptions: PaymentTypeOption[] = [
    { value: 1, label: intl.formatMessage(messages.paymentTypeCheck) },
    { value: 0, label: intl.formatMessage(messages.paymentTypeOther) },
  ]

  const validationSchema = Yup.object({
    type: Yup.object()
      .nullable()
      .required(intl.formatMessage(messages.paymentTypeRequiredError)),
    currency: Yup.object()
      .nullable()
      .required(intl.formatMessage(messages.currencyRequiredError)),
  })

  const formik = useFormik<VendorPaymentMethodFormSchema>({
    initialValues: {
      name: '',
      type: null,
      // Pre-select the card currency (BHV-047) so the created method matches
      // the bill's currency and survives the payment-method `matchesCurrency`
      // filter. Only the id is known here; the core CurrencySelect matches on
      // it via getOptionValue.
      currency: defaultCurrencyId
        ? ({ id: defaultCurrencyId } as Currency)
        : null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      onSubmit(values)
      setSubmitting(false)
    },
  })

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space>
          <Flex.Row>
            <Flex.Item width={1 / 3}>
              <Form.TextField
                id='name'
                name='name'
                label={intl.formatMessage(messages.nameLabel)}
                placeholder={intl.formatMessage(messages.namePlaceholder)}
                autoFocus
              />
            </Flex.Item>
            <Flex.Item width={1 / 3}>
              <Form.Select
                id='type'
                name='type'
                label={intl.formatMessage(messages.paymentTypeLabel)}
                placeholder={intl.formatMessage(
                  messages.paymentTypePlaceholder
                )}
                options={paymentTypeOptions}
                required
              />
            </Flex.Item>
            <Flex.Item width={1 / 3}>
              <WithFormControlSelect
                id='currency'
                name='currency'
                label={intl.formatMessage(messages.currencyLabel)}
                required
              >
                <CurrencySelect
                  placeholder={intl.formatMessage(messages.currencyPlaceholder)}
                />
              </WithFormControlSelect>
            </Flex.Item>
          </Flex.Row>

          <Divider />
          <Space direction='horizontal' justify='end' align='center'>
            <TertiaryButton
              type='button'
              onClick={onCancel}
              disabled={isSaving}
            >
              {intl.formatMessage(messages.cancelButton)}
            </TertiaryButton>
            <PrimaryButton type='submit' isLoading={isSaving}>
              {intl.formatMessage(messages.addButton)}
            </PrimaryButton>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}

VendorPaymentMethodForm.displayName = 'VendorPaymentMethodForm'
